import type { OrderDetail } from "@/modules/orders/domain/types";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatLak(n: number): string {
  return n.toLocaleString("lo-LA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

/**
 * สร้าง HTML สำหรับบิล/ใบเสร็จ จาก order detail
 */
export function buildOrderBillHtml(order: OrderDetail): string {
  const dateStr = formatDateTimeLocal(order.createdAt);
  const itemsRows = order.items
    .map((item) => {
      const displayName =
        item.variantName && item.variantName !== "Default"
          ? `${item.productName} (${item.variantName})`
          : item.productName;
      const modLines =
        item.modifiers.length > 0
          ? item.modifiers
              .map(
                (m) =>
                  `+ ${m.name}${
                    m.priceDelta !== 0 ? ` ${formatLak(m.priceDelta)}` : ""
                  }`,
              )
              .join(", ")
          : "";
      return `
        <tr>
          <td style="padding:4px 8px;border-bottom:1px solid #eee;vertical-align:top">
            <strong>${escapeHtml(displayName)}</strong>
            ${modLines ? `<div style="font-size:11px;color:#666;margin-top:2px">${escapeHtml(modLines)}</div>` : ""}
          </td>
          <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:center">${item.qty}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${formatLak(item.unitPrice)}</td>
          <td style="padding:4px 8px;border-bottom:1px solid #eee;text-align:right">${formatLak(item.lineTotal)}</td>
        </tr>`;
    })
    .join("");

  const customerLine =
    order.customerName || order.customerPhone
      ? `<p style="margin:4px 0"><strong>ລູກຄ້າ:</strong> ${escapeHtml(order.customerName ?? "-")} ${order.customerPhone ? " | " + escapeHtml(order.customerPhone) : ""}</p>`
      : "";

  const pickupLine = order.pickupAt
    ? `<p style="margin:4px 0"><strong>ວັນນັບຮັບ:</strong> ${formatDateTimeLocal(order.pickupAt)}</p>`
    : "";

  return `<!DOCTYPE html>
<html lang="lo">
<head>
  <meta charset="utf-8">
  <title>ບິນ Order #${escapeHtml(order.orderNo)}</title>
  <style>
    body { font-family: system-ui, sans-serif; font-size: 14px; line-height: 1.4; color: #111; max-width: 400px; margin: 16px auto; padding: 0 12px; }
    h1 { font-size: 18px; margin: 0 0 4px 0; }
    .meta { color: #555; font-size: 12px; margin-bottom: 12px; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; padding: 6px 8px; border-bottom: 1px solid #333; font-size: 12px; }
    .total-row { font-weight: bold; border-top: 1px solid #333; }
    .foot { margin-top: 16px; font-size: 11px; color: #666; text-align: center; }
  </style>
</head>
<body>
  <h1>Mineral Coffee</h1>
  <div class="meta">
    <p style="margin:2px 0"><strong>Order #${escapeHtml(order.orderNo)}</strong></p>
    <p style="margin:2px 0">${dateStr}</p>
    <p style="margin:2px 0">ສາຂາ: ${escapeHtml(order.branchName)}</p>
    ${customerLine}
    ${pickupLine}
  </div>
  <table>
    <thead>
      <tr>
        <th>ສິນຄ້າ</th>
        <th style="text-align:center;width:50px">ຈຳນວນ</th>
        <th style="text-align:right;width:70px">ລາຄາ</th>
        <th style="text-align:right;width:80px">ຍອດ</th>
      </tr>
    </thead>
    <tbody>
      ${itemsRows}
    </tbody>
  </table>
  <table style="margin-top:8px">
    <tr><td style="padding:2px 8px">ຍອດລວມ</td><td style="text-align:right;padding:2px 8px">${formatLak(order.subtotal)} ${order.currency}</td></tr>
    ${order.discountTotal > 0 ? `<tr><td style="padding:2px 8px">ສ່ວນລົດ</td><td style="text-align:right;padding:2px 8px">-${formatLak(order.discountTotal)} ${order.currency}</td></tr>` : ""}
    <tr class="total-row"><td style="padding:6px 8px">ຍອດສຸດທິ</td><td style="text-align:right;padding:6px 8px">${formatLak(order.total)} ${order.currency}</td></tr>
  </table>
  <div class="foot">ພິມຈາກ HQ Admin · ${dateStr}</div>
  <script>
    window.onload = function() { window.print(); };
    window.onafterprint = function() { window.close(); };
  </script>
</body>
</html>`;
}

/**
 * เปิดแท็บใหม่แสดงบิล แล้วขึ้น print dialog; หลังกด Print หรือ Cancel จะปิดแท็บ
 */
export function openOrderBillPrint(order: OrderDetail): void {
  const html = buildOrderBillHtml(order);
  const w = window.open("", "_blank");
  if (!w) {
    return;
  }
  w.document.write(html);
  w.document.close();
}
